import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  formatUsageMoney,
  getTransactionUsageFee,
  formatPricePerTransaction,
  isBillableTransaction,
} from '../config/billing';
import { formatDateTime } from '../utils/csvParser';
import billingApi from '../services/billingApi';
import { useAuth } from '../auth/useAuth';

function formatBillingPeriodLabel(label) {
  if (!label || !/^\d{4}-\d{2}$/.test(label)) return null;
  const [year, month] = label.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
  });
}

function formatNextBillingRun(isoString) {
  if (!isoString) return null;
  return new Date(isoString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function formatTrialExpiry(isoString) {
  if (!isoString) return null;
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function usageAmountColor(amount) {
  if (amount < 0) return 'var(--color-olive)';
  return 'var(--color-sage)';
}

function isMobileViewport() {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;
}

function UsageDetailsPanel({ transactions, billingProfile, dateRangeLabel, onSummary, initialSummary }) {
  const { getAccessToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [mobileSheet, setMobileSheet] = useState(false);
  const [tab, setTab] = useState('fees');
  const [periodSummary, setPeriodSummary] = useState(initialSummary ?? null);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [periodError, setPeriodError] = useState('');
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (initialSummary) {
      setPeriodSummary(initialSummary);
    }
  }, [initialSummary]);

  const currency =
    billingProfile?.currency ||
    periodSummary?.currentPeriod?.currency ||
    transactions.find((t) => t.currency)?.currency ||
    'cad';
  const unitPrice =
    billingProfile?.pricePerTransactionDollars ??
    periodSummary?.pricePerTransactionDollars ??
    null;

  const billableTxns = transactions.filter(
    (t) => isBillableTransaction(t) && getTransactionUsageFee(t) > 0
  );
  const feesTotal = billableTxns.reduce((sum, txn) => sum + getTransactionUsageFee(txn), 0);
  const displayTxns = transactions;

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const load = async () => {
      try {
        setPeriodLoading(true);
        setPeriodError('');
        const accessToken = (await getAccessToken()) || localStorage.getItem('accessToken');
        if (!accessToken) {
          if (!cancelled) setPeriodError('Session expired. Please sign in again.');
          return;
        }
        const data = await billingApi.getUsageSummary(accessToken);
        if (!cancelled) {
          setPeriodSummary(data);
          if (onSummary) onSummary(data);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err.message || 'Could not load period usage';
          setPeriodError(
            /expired|not authorized|401/i.test(message)
              ? 'Session expired. Please refresh or sign in again.'
              : message
          );
        }
      } finally {
        if (!cancelled) setPeriodLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, getAccessToken]);

  useEffect(() => {
    if (!open) return undefined;

    const onDocClick = (e) => {
      const inTrigger = wrapRef.current?.contains(e.target);
      const inPanel = panelRef.current?.contains(e.target);
      if (!inTrigger && !inPanel) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setMobileSheet(false);
      return undefined;
    }

    const syncMobile = () => setMobileSheet(isMobileViewport());
    syncMobile();
    window.addEventListener('resize', syncMobile);
    return () => window.removeEventListener('resize', syncMobile);
  }, [open]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [open, tab]);

  // iOS Safari ignores body { overflow: hidden }, so freeze the body with
  // position: fixed and restore the scroll offset on close.
  useEffect(() => {
    if (!open || !mobileSheet) return undefined;
    const scrollY = window.scrollY;
    const { body } = document;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      overflow: body.style.overflow,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.overflow = 'hidden';
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [open, mobileSheet]);

  const period = periodSummary?.currentPeriod;
  const periodCurrency = period?.currency || currency;
  const summaryLoaded = period && typeof period.totalAmount === 'number';
  const adjustedTotal = summaryLoaded ? period.totalAmount : feesTotal;
  const showFeesTotal = summaryLoaded || (!periodLoading && !!periodError);
  const showTrialCredit =
    period?.hasTrial && period?.trialActive && period?.creditsRemaining > 0;
  const showTrialSection =
    summaryLoaded && (period?.hasTrial || (period?.trialCredits ?? 0) > 0);
  const priceLabel = formatPricePerTransaction({
    currency,
    pricePerTransactionDollars: unitPrice ?? undefined,
    countryCode: billingProfile?.countryCode,
  });

  const creditUnit = period?.unitAmount ?? unitPrice ?? 1;
  const creditsApplied =
    period?.creditsApplied ?? period?.creditsUsed ?? 0;
  const trialCreditsTotal = period?.trialCredits ?? 0;
  const grossUsage = period?.grossAmount ?? (period?.transactionCount ?? 0) * creditUnit;
  const hasCreditLines = showTrialSection || creditsApplied > 0 || showTrialCredit;

  const breakdownRow = (label, value, opts = {}) => (
    <div
      className={`usage-breakdown-row${opts.credit ? ' usage-breakdown-row--credit' : ''}`}
    >
      <span className="usage-breakdown-label">{label}</span>
      <span className="usage-breakdown-value">{value}</span>
    </div>
  );

  const creditBreakdown = hasCreditLines ? (
    <div className="usage-breakdown">
      {showTrialSection &&
        breakdownRow(
          `Trial credit included${period.trialEndsAt ? ` · expires ${formatTrialExpiry(period.trialEndsAt)}` : ''}`,
          formatUsageMoney(trialCreditsTotal * creditUnit, periodCurrency),
          { credit: true }
        )}
      {breakdownRow(
        `Usage (${period?.transactionCount ?? 0} × ${formatUsageMoney(creditUnit, periodCurrency)})`,
        formatUsageMoney(grossUsage, periodCurrency)
      )}
      {creditsApplied > 0 &&
        breakdownRow(
          `Credit applied (${creditsApplied})`,
          `−${formatUsageMoney(creditsApplied * creditUnit, periodCurrency)}`,
          { credit: true }
        )}
      {period?.hasTrial &&
        period?.trialActive &&
        breakdownRow(
          'Credit remaining',
          `−${formatUsageMoney((period.creditsRemaining ?? 0) * creditUnit, periodCurrency)}`,
          { credit: true }
        )}
    </div>
  ) : periodLoading ? (
    <div className="usage-breakdown usage-breakdown--loading">
      <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>
        Loading trial credit…
      </p>
    </div>
  ) : null;

  const creditBrief = hasCreditLines ? (
    <div className="usage-credit-brief">
      <span className="usage-credit-brief-line">
        Credit {formatUsageMoney(trialCreditsTotal * creditUnit, periodCurrency)}
        {' · '}used {formatUsageMoney(creditsApplied * creditUnit, periodCurrency)}
        {' · '}left {formatUsageMoney((period?.creditsRemaining ?? 0) * creditUnit, periodCurrency)}
      </span>
      <strong style={{ color: usageAmountColor(period?.totalAmount ?? 0) }}>
        {formatUsageMoney(period?.totalAmount ?? 0, periodCurrency)}
      </strong>
    </div>
  ) : periodLoading ? (
    <div className="usage-credit-brief">
      <span className="usage-credit-brief-line">Loading credit…</span>
    </div>
  ) : null;

  const balanceRow = (
    <div className="usage-balance-row">
      <span>{hasCreditLines ? 'Balance' : `${billableTxns.length} billable in view`}</span>
      <strong style={{ color: usageAmountColor(summaryLoaded ? period.totalAmount : adjustedTotal) }}>
        {showFeesTotal
          ? formatUsageMoney(summaryLoaded ? period.totalAmount : adjustedTotal, periodCurrency)
          : '…'}
      </strong>
    </div>
  );

  const transactionList = (
    <div className="usage-txn-list">
      {displayTxns.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 13, margin: 0 }}>
          No transactions in the current view.
        </p>
      ) : (
        displayTxns.map((txn, i) => {
          const billable = isBillableTransaction(txn) && getTransactionUsageFee(txn) > 0;
          return (
            <div key={txn.transaction_id || i} className="usage-txn-row">
              <span className="usage-txn-id">
                {txn.transaction_id}
                {txn.dateTime ? (
                  <span className="usage-txn-date">{formatDateTime(txn.dateTime)}</span>
                ) : null}
                {!billable ? (
                  <span className="usage-txn-badge">Unaccepted</span>
                ) : null}
              </span>
              <span className={`usage-txn-fee${billable ? '' : ' usage-txn-fee--muted'}`}>
                {billable
                  ? formatUsageMoney(getTransactionUsageFee(txn), txn.currency || currency)
                  : '—'}
              </span>
            </div>
          );
        })
      )}
    </div>
  );

  const feesTab = (
    <>
      {periodError && (
        <div className="provider-alert provider-alert--error usage-panel-error">
          {periodError}
        </div>
      )}
      {creditBreakdown}
      {balanceRow}
      {transactionList}
    </>
  );

  const periodMeta = period ? (
    <>
      {periodSummary?.testClient && (
        <p className="usage-panel-note usage-panel-note--success">
          Test account — not invoiced automatically.
        </p>
      )}
      <p className="usage-panel-note">
        {new Date(period.periodStart).toLocaleDateString()} –{' '}
        {new Date(period.periodEnd).toLocaleDateString()}
        {period.lastBilledPeriod
          ? ` · after ${formatBillingPeriodLabel(period.lastBilledPeriod) || period.lastBilledPeriod}`
          : ' · current month'}
      </p>
      {period.nextBillingRunAt && (
        <p className="usage-panel-note">
          Next invoice: {formatNextBillingRun(period.nextBillingRunAt)}
        </p>
      )}
    </>
  ) : null;

  const periodTab = !periodLoading && !periodError && period ? (
    <>
      {periodMeta}
      {!mobileSheet && creditBreakdown}
      {!mobileSheet && (
        <div className="usage-balance-row usage-balance-row--lg">
          <span>{hasCreditLines ? 'Balance' : `${period.transactionCount} billable`}</span>
          <strong style={{ color: usageAmountColor(period.totalAmount) }}>
            {formatUsageMoney(period.totalAmount, periodCurrency)}
          </strong>
        </div>
      )}
      <p className="usage-panel-foot">Unbilled usage since your last paid invoice.</p>
    </>
  ) : periodLoading ? (
    <p className="usage-panel-note">Loading period summary…</p>
  ) : periodError ? (
    <div className="provider-alert provider-alert--error usage-panel-error">{periodError}</div>
  ) : null;

  const panelBody = (
    <>
      <p className="usage-panel-intro">
        {priceLabel} per billable transaction
        {billingProfile?.regionLabel ? ` · ${billingProfile.regionLabel}` : ''}
        {dateRangeLabel ? <span className="usage-panel-range">Range: {dateRangeLabel}</span> : null}
      </p>

      <div className="usage-tabs">
        <button
          type="button"
          className={`usage-tab${tab === 'fees' ? ' usage-tab--active' : ''}`}
          onClick={() => setTab('fees')}
        >
          Fees
        </button>
        <button
          type="button"
          className={`usage-tab${tab === 'period' ? ' usage-tab--active' : ''}`}
          onClick={() => setTab('period')}
        >
          Since last invoice
        </button>
      </div>

      <div ref={scrollRef} className="usage-panel-scroll">
        {tab === 'fees' ? feesTab : periodTab}
      </div>
    </>
  );

  const closePanel = () => setOpen(false);

  const panel = (
    <div
      ref={panelRef}
      className={`usage-dropdown${mobileSheet ? ' usage-dropdown--sheet' : ''}`}
      role="dialog"
      aria-label="Usage details"
    >
      {mobileSheet && (
        <div className="usage-sheet-toolbar">
          <strong className="usage-sheet-title">Usage</strong>
          <button type="button" className="usage-sheet-close" onClick={closePanel} aria-label="Close">
            ×
          </button>
        </div>
      )}
      {mobileSheet ? (
        <>
          <div className="usage-tabs usage-tabs--sheet">
            <button
              type="button"
              className={`usage-tab${tab === 'fees' ? ' usage-tab--active' : ''}`}
              onClick={() => setTab('fees')}
            >
              Transactions ({displayTxns.length})
            </button>
            <button
              type="button"
              className={`usage-tab${tab === 'period' ? ' usage-tab--active' : ''}`}
              onClick={() => setTab('period')}
            >
              Since last invoice
            </button>
          </div>
          <div ref={scrollRef} className="usage-panel-scroll usage-panel-scroll--sheet">
            {periodError && (
              <div className="provider-alert provider-alert--error usage-panel-error">
                {periodError}
              </div>
            )}
            {tab === 'fees' ? (
              <>
                {creditBrief}
                {transactionList}
              </>
            ) : (
              periodTab
            )}
          </div>
        </>
      ) : (
        panelBody
      )}
    </div>
  );

  const openUsagePanel = () => {
    setOpen((wasOpen) => !wasOpen);
  };

  return (
    <>
      <div ref={wrapRef} className="usage-panel-wrap" style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={openUsagePanel}
          className="provider-btn provider-btn--secondary provider-btn--sm provider-btn--inline"
          aria-expanded={open}
        >
          Usage
        </button>
        {open && !mobileSheet && panel}
      </div>

      {open &&
        mobileSheet &&
        createPortal(
          <>
            <button
              type="button"
              className="usage-backdrop"
              aria-label="Close usage panel"
              onClick={closePanel}
            />
            {panel}
          </>,
          document.body
        )}
    </>
  );
}

export default UsageDetailsPanel;
